import { useMatches } from "@tanstack/react-router";
import { useEffect } from "react";

/**
 * Every address this app answers on, and what each screen is called.
 *
 * The router (`lib/router.tsx`) builds its routes from these paths, the rail
 * links to them, and the tab's title reads its words from `SCREEN_NAMES`. One
 * table, so the three cannot drift apart.
 */
export const PATHS = {
  home: "/",
  overview: "/overview",
  prompts: "/prompts",
  rivals: "/rivals",
  opportunities: "/opportunities",
  citations: "/citations",
  // Where to publish. The path is main's; "off-page" is an SEO phrase, so the
  // rail and the tab say "Where to publish" instead.
  offPage: "/off-page",
  settings: "/settings/brand",
  research: "/research",
  library: "/library",
  addPrompts: "/library/add",
  fanout: "/tools/fanout",
  urlPrompts: "/tools/page-prompts",
  newBrand: "/brands/new",
  profile: "/settings/profile",
  members: "/settings/members",
  aiApps: "/settings/ai-apps",
} as const;

/**
 * Which screen is showing. Each route carries one in its `staticData`, so the
 * title and the report chooser read it from the match rather than re-parsing
 * the address.
 */
export type ScreenKind =
  Exclude<keyof typeof PATHS, "home"> | "fanout-study" | "not-found";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** Which screen a route draws. Set on every route in `router.tsx`. */
    readonly screen?: ScreenKind;
  }
}

/**
 * The screen the address resolved to: the deepest match's `staticData`.
 *
 * A match with none is the signed-in layout alone, which only happens for an
 * address no route claims — so "not-found" is the answer, not a guess.
 */
export function useScreenKind(): ScreenKind {
  return useMatches({
    select: (matches) => matches.at(-1)?.staticData.screen ?? "not-found",
  });
}

/**
 * What the rail calls each screen, and so what its tab is called.
 *
 * The same words in both places on purpose: a tab titled differently from the
 * link that opened it reads as a different page.
 *
 * Renamed in the second revamp (2026-10-12), the addresses kept so every
 * bookmark and emailed link still opens: the Overview is the Brief, Prompts
 * are Questions — the word a customer uses — Citations are Sources, and the
 * two ways of finding questions are one screen, Find questions.
 */
export const SCREEN_NAMES: Readonly<Record<ScreenKind, string>> = {
  overview: "Brief",
  prompts: "Questions",
  rivals: "Rivals",
  opportunities: "Opportunities",
  citations: "Sources",
  offPage: "Where to publish",
  settings: "Brand settings",
  research: "Find questions",
  library: "Library",
  addPrompts: "Add questions",
  fanout: "Fan-out",
  "fanout-study": "Fan-out",
  urlPrompts: "Find questions",
  newBrand: "Add a brand",
  profile: "Profile",
  members: "People",
  aiApps: "AI apps",
  "not-found": "Page not found",
};

/**
 * Where a screen about one brand's report lives, or `null` for any other screen.
 *
 * Those are the screens whose address names the report, so this is what the
 * report chooser rewrites and what puts the brand into the tab's title.
 */
export function reportScreenPath(kind: ScreenKind): string | null {
  switch (kind) {
    case "overview":
    case "prompts":
    case "rivals":
    case "opportunities":
    case "citations":
    case "offPage":
    case "settings":
      return PATHS[kind];
    default:
      return null;
  }
}

/**
 * The browser tab's text: most specific first, the product last.
 *
 * "Sources · Merc · Crux" — so five open tabs are five different things
 * rather than five copies of "Crux". A missing part is left out, not shown
 * as a blank between two dots.
 */
export function documentTitle(...parts: readonly (string | null)[]): string {
  return [...parts.filter((part) => part !== null && part !== ""), "Crux"].join(" · ");
}

/** Keeps the tab's title in step with the screen on it. */
export function usePageTitle(title: string): void {
  useEffect(() => {
    document.title = title;
  }, [title]);
}
